import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	deleteOrderDiscount,
	getAllOrderDiscounts,
	getOrders,
	setOrders,
	upsertOrderDiscount,
} from "../../api/apiServiceFetch";
import "./OrderDiscounts.scss";

const SEX_LABELS = {
	1: "Мужчины",
	2: "Женщины",
};

const TYPE_LABELS = {
	1: "Подписка",
	2: "Огонь",
	3: "Чистый лист",
	5: "Повторная регистрация",
	10: "Инвестиция",
	100: "Platinum",
};

const formatMoney = (value, currency) =>
	`${new Intl.NumberFormat("ru-RU", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 2,
	}).format(Number(value || 0))} ${currency || ""}`.trim();

const orderLabel = (order) => {
	if (!order) return "-";
	const typeText = TYPE_LABELS[order.type] || `type ${order.type}`;
	return `${typeText} · ${order.charId} (${formatMoney(order.price, order.currency)})`;
};

const previewPrice = (price, percent) => {
	const p = Number(price || 0);
	const pct = Math.max(0, Math.min(100, Number(percent || 0)));
	return Math.round(p * (100 - pct)) / 100;
};

const OrderDiscounts = () => {
	const [orders, setOrdersState] = useState([]);
	const [discounts, setDiscounts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [saving, setSaving] = useState(false);

	// Tariff edit drafts keyed by orderId
	const [tariffDrafts, setTariffDrafts] = useState({});

	const [form, setForm] = useState({
		id: 0,
		orderId: 0,
		sex: 2,
		percent: 0,
		isActive: true,
	});

	const ordersById = useMemo(() => {
		const map = new Map();
		for (const o of orders) {
			map.set(Number(o.orderId), o);
		}
		return map;
	}, [orders]);

	const loadAll = useCallback(async () => {
		setLoading(true);
		setError("");
		try {
			const [ordersData, discountsData] = await Promise.all([
				getOrders().catch(() => []),
				getAllOrderDiscounts().catch(() => []),
			]);

			// 'paylink' — служебная строка Orders, к которой цепляются платежные
			// ссылки из админки. Это не тариф, редактировать его нечего.
			setOrdersState(
				Array.isArray(ordersData)
					? ordersData.filter((order) => order?.charId !== "paylink")
					: [],
			);
			setDiscounts(Array.isArray(discountsData) ? discountsData : []);
			setTariffDrafts({});
		} catch (err) {
			setError(err?.message || "Не удалось загрузить данные.");
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void loadAll();
	}, [loadAll]);

	useEffect(() => {
		if (!form.orderId && orders[0]?.orderId) {
			setForm((prev) => ({ ...prev, orderId: orders[0].orderId }));
		}
	}, [orders, form.orderId]);

	const previewOrder = ordersById.get(Number(form.orderId));
	const previewNewPrice = previewOrder
		? previewPrice(previewOrder.price, form.percent)
		: null;

	const draftFor = (order) => {
		const draft = tariffDrafts[order.orderId];
		return draft
			? draft
			: {
					price: String(order.price ?? ""),
					isActive: !!order.isActive,
					fire: String(order.fire ?? 0),
					clean: String(order.clean ?? 0),
				};
	};

	const updateTariffDraft = (orderId, patch) => {
		setTariffDrafts((prev) => {
			const order = ordersById.get(orderId);
			const current = prev[orderId] || {
				price: String(order?.price ?? ""),
				isActive: !!order?.isActive,
				fire: String(order?.fire ?? 0),
				clean: String(order?.clean ?? 0),
			};
			return { ...prev, [orderId]: { ...current, ...patch } };
		});
	};

	const saveTariff = async (order) => {
		const draft = draftFor(order);
		const nextPrice = Number(draft.price);
		const nextFire = Number(draft.fire);
		const nextClean = Number(draft.clean);

		if (!Number.isFinite(nextPrice) || nextPrice < 0) {
			setError("Цена должна быть числом ≥ 0.");
			return;
		}
		if (!Number.isFinite(nextFire) || nextFire < 0) {
			setError("Огонь должен быть числом ≥ 0.");
			return;
		}
		if (!Number.isFinite(nextClean) || nextClean < 0) {
			setError("Чистый лист должен быть числом ≥ 0.");
			return;
		}

		try {
			setSaving(true);
			setError("");
			await setOrders({
				id: order.orderId,
				type: order.type,
				charId: order.charId,
				currency: order.currency,
				price: nextPrice,
				isActive: !!draft.isActive,
				fire: nextFire,
				period: order.period ?? 0,
				clean: nextClean,
			});
			await loadAll();
		} catch (err) {
			setError(err?.message || "Не удалось сохранить тариф.");
		} finally {
			setSaving(false);
		}
	};

	const handleSave = async (event) => {
		event.preventDefault();

		if (!form.orderId) {
			setError("Выберите заказ.");
			return;
		}

		if (form.sex !== 1 && form.sex !== 2) {
			setError("Пол должен быть 1 (муж) или 2 (жен).");
			return;
		}

		const percent = Math.max(0, Math.min(100, Number(form.percent || 0)));

		try {
			setSaving(true);
			setError("");
			await upsertOrderDiscount({
				id: Number(form.id || 0),
				orderId: Number(form.orderId),
				sex: Number(form.sex),
				percent,
				isActive: !!form.isActive,
			});
			setForm({
				id: 0,
				orderId: form.orderId,
				sex: form.sex,
				percent: 0,
				isActive: true,
			});
			await loadAll();
		} catch (err) {
			setError(err?.message || "Не удалось сохранить скидку.");
		} finally {
			setSaving(false);
		}
	};

	const handleEdit = (row) => {
		setForm({
			id: row.id,
			orderId: row.orderId,
			sex: row.sex,
			percent: row.percent,
			isActive: row.isActive,
		});
	};

	const handleDelete = async (row) => {
		// eslint-disable-next-line no-restricted-globals, no-alert
		if (!window.confirm(`Удалить скидку #${row.id}?`)) return;

		try {
			setSaving(true);
			await deleteOrderDiscount(row.id);
			await loadAll();
		} catch (err) {
			setError(err?.message || "Не удалось удалить скидку.");
		} finally {
			setSaving(false);
		}
	};

	const handleToggleActive = async (row, nextActive) => {
		try {
			setSaving(true);
			await upsertOrderDiscount({
				id: row.id,
				orderId: row.orderId,
				sex: row.sex,
				percent: row.percent,
				isActive: nextActive,
			});
			await loadAll();
		} catch (err) {
			setError(err?.message || "Не удалось обновить скидку.");
		} finally {
			setSaving(false);
		}
	};

	const tariffDirty = (order) => {
		const draft = tariffDrafts[order.orderId];
		if (!draft) return false;
		return (
			Number(draft.price) !== Number(order.price) ||
			!!draft.isActive !== !!order.isActive ||
			Number(draft.fire) !== Number(order.fire ?? 0) ||
			Number(draft.clean) !== Number(order.clean ?? 0)
		);
	};

	return (
		<div className="order-discounts-page">
			<header className="order-discounts-header">
				<h2>Тарифы и скидки</h2>
				<p>
					Управляйте ценами тарифов и персональными скидками по полу. Скидка
					применяется поверх базовой цены тарифа.
				</p>
			</header>

			{error ? (
				<div className="order-discounts-state order-discounts-state--error">
					{error}
				</div>
			) : null}

			<section className="order-discounts-section">
				<h3 className="order-discounts-section__title">Тарифы</h3>
				<div className="order-discounts-table-wrap">
					<table className="order-discounts-table">
						<thead>
							<tr>
								<th>ID</th>
								<th>Тип</th>
								<th>CharId</th>
								<th>Валюта</th>
								<th>Цена</th>
								<th>Огонь</th>
								<th>Чистый лист</th>
								<th>Активен</th>
								<th>Действия</th>
							</tr>
						</thead>
						<tbody>
							{loading ? (
								<tr>
									<td colSpan={9} className="order-discounts-empty">
										Загружаем...
									</td>
								</tr>
							) : orders.length === 0 ? (
								<tr>
									<td colSpan={9} className="order-discounts-empty">
										Тарифов пока нет
									</td>
								</tr>
							) : (
								orders.map((order) => {
									const draft = draftFor(order);
									return (
										<tr key={order.orderId}>
											<td data-label="ID">{order.orderId}</td>
											<td data-label="Тип">
												{TYPE_LABELS[order.type] || `type ${order.type}`}
											</td>
											<td data-label="CharId">{order.charId}</td>
											<td data-label="Валюта">{order.currency}</td>
											<td data-label="Цена">
												<input
													className="order-discounts-inline"
													type="number"
													min="0"
													step="0.01"
													value={draft.price}
													disabled={saving}
													onChange={(event) =>
														updateTariffDraft(order.orderId, {
															price: event.target.value,
														})
													}
												/>
											</td>
											<td data-label="Огонь">
												<input
													className="order-discounts-inline order-discounts-inline--narrow"
													type="number"
													min="0"
													step="1"
													value={draft.fire}
													disabled={saving}
													onChange={(event) =>
														updateTariffDraft(order.orderId, {
															fire: event.target.value,
														})
													}
												/>
											</td>
											<td data-label="Чистый лист">
												<input
													className="order-discounts-inline order-discounts-inline--narrow"
													type="number"
													min="0"
													step="1"
													value={draft.clean}
													disabled={saving}
													onChange={(event) =>
														updateTariffDraft(order.orderId, {
															clean: event.target.value,
														})
													}
												/>
											</td>
											<td data-label="Активен">
												<label className="order-discounts-toggle">
													<input
														type="checkbox"
														checked={!!draft.isActive}
														disabled={saving}
														onChange={(event) =>
															updateTariffDraft(order.orderId, {
																isActive: event.target.checked,
															})
														}
													/>
													<span>{draft.isActive ? "вкл" : "выкл"}</span>
												</label>
											</td>
											<td data-label="Действия">
												<div className="order-discounts-actions">
													<button
														type="button"
														disabled={saving || !tariffDirty(order)}
														onClick={() => saveTariff(order)}
													>
														Сохранить
													</button>
												</div>
											</td>
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				</div>
			</section>

			<section className="order-discounts-section">
				<h3 className="order-discounts-section__title">Скидки по полу</h3>
				<form className="order-discounts-form" onSubmit={handleSave}>
					<div className="order-discounts-form__row">
						<label>
							<span>Тариф</span>
							<select
								value={form.orderId}
								onChange={(event) =>
									setForm((prev) => ({
										...prev,
										orderId: Number(event.target.value || 0),
									}))
								}
							>
								<option value={0} disabled>
									-- выберите --
								</option>
								{orders.map((order) => (
									<option key={order.orderId} value={order.orderId}>
										#{order.orderId} · {orderLabel(order)}
									</option>
								))}
							</select>
						</label>

						<label>
							<span>Пол</span>
							<select
								value={form.sex}
								onChange={(event) =>
									setForm((prev) => ({
										...prev,
										sex: Number(event.target.value),
									}))
								}
							>
								<option value={2}>Женщины</option>
								<option value={1}>Мужчины</option>
							</select>
						</label>

						<label>
							<span>Скидка, %</span>
							<input
								type="number"
								min="0"
								max="100"
								step="1"
								value={form.percent}
								onChange={(event) =>
									setForm((prev) => ({
										...prev,
										percent: event.target.value,
									}))
								}
							/>
						</label>

						<label className="order-discounts-form__check">
							<input
								type="checkbox"
								checked={!!form.isActive}
								onChange={(event) =>
									setForm((prev) => ({
										...prev,
										isActive: event.target.checked,
									}))
								}
							/>
							<span>Активна</span>
						</label>

						<div className="order-discounts-form__actions">
							<button type="submit" disabled={saving || loading}>
								{form.id ? "Обновить" : "Создать"}
							</button>
							{form.id ? (
								<button
									type="button"
									className="secondary"
									onClick={() =>
										setForm({
											id: 0,
											orderId: form.orderId,
											sex: form.sex,
											percent: 0,
											isActive: true,
										})
									}
								>
									Отмена
								</button>
							) : null}
						</div>
					</div>

					{previewOrder ? (
						<div className="order-discounts-form__preview">
							<div>
								<span>Текущая цена</span>
								<strong>
									{formatMoney(previewOrder.price, previewOrder.currency)}
								</strong>
							</div>
							<div>
								<span>Цена со скидкой</span>
								<strong>
									{formatMoney(previewNewPrice, previewOrder.currency)}
								</strong>
							</div>
						</div>
					) : null}
				</form>

				<div className="order-discounts-table-wrap">
					<table className="order-discounts-table">
						<thead>
							<tr>
								<th>ID</th>
								<th>Тариф</th>
								<th>Пол</th>
								<th>% скидки</th>
								<th>Цена после</th>
								<th>Активна</th>
								<th>Действия</th>
							</tr>
						</thead>
						<tbody>
							{loading ? (
								<tr>
									<td colSpan={7} className="order-discounts-empty">
										Загружаем...
									</td>
								</tr>
							) : discounts.length === 0 ? (
								<tr>
									<td colSpan={7} className="order-discounts-empty">
										Скидок ещё нет
									</td>
								</tr>
							) : (
								discounts.map((row) => {
									const order = ordersById.get(Number(row.orderId));
									return (
										<tr key={row.id}>
											<td data-label="ID">{row.id}</td>
											<td data-label="Тариф">{orderLabel(order)}</td>
											<td data-label="Пол">{SEX_LABELS[row.sex] || row.sex}</td>
											<td data-label="% скидки">{row.percent}%</td>
											<td data-label="Цена после">
												{order
													? formatMoney(
															previewPrice(order.price, row.percent),
															order.currency,
														)
													: "-"}
											</td>
											<td data-label="Активна">
												<label className="order-discounts-toggle">
													<input
														type="checkbox"
														checked={!!row.isActive}
														disabled={saving}
														onChange={(event) =>
															handleToggleActive(row, event.target.checked)
														}
													/>
													<span>{row.isActive ? "вкл" : "выкл"}</span>
												</label>
											</td>
											<td data-label="Действия">
												<div className="order-discounts-actions">
													<button
														type="button"
														disabled={saving}
														onClick={() => handleEdit(row)}
													>
														Изменить
													</button>
													<button
														type="button"
														className="danger"
														disabled={saving}
														onClick={() => handleDelete(row)}
													>
														Удалить
													</button>
												</div>
											</td>
										</tr>
									);
								})
							)}
						</tbody>
					</table>
				</div>
			</section>
		</div>
	);
};

export default OrderDiscounts;
